import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import LoadingFallback from '../components/LoadingFallback';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import { useAuth } from '../contexts/AuthContext';
import type { EventCategory, EventType } from '../api/types';

const CATEGORY_LABELS: Record<EventCategory, string> = {
  track: 'Track',
  field: 'Field',
  relay: 'Relay',
  multi: 'Multi-events',
};

interface EventFormPayload {
  code: string;
  name: string;
  category: EventCategory;
  unit: string;
  is_relay: boolean;
  distance_meters?: number;
}

export default function EventLibraryPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { confirm, toast } = useFeedback();
  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; event: EventType } | null>(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const eventsQuery = useQuery<EventType[]>({
    queryKey: ['event-types'],
    queryFn: async () => (await api.get('/event-types')).data,
  });

  const groups = useMemo(() => {
    const out: Record<string, EventType[]> = {};
    for (const e of eventsQuery.data ?? []) {
      const k = e.category;
      out[k] ??= [];
      out[k].push(e);
    }
    return out;
  }, [eventsQuery.data]);

  const createMutation = useMutation({
    mutationFn: async (payload: EventFormPayload) =>
      (await api.post('/event-types', payload)).data,
    onSuccess: () => {
      toast({ message: 'Event added', severity: 'success' });
      setDialog(null);
      qc.invalidateQueries({ queryKey: ['event-types'] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: EventFormPayload }) =>
      (await api.patch(`/event-types/${id}`, payload)).data,
    onSuccess: () => {
      toast({ message: 'Event updated', severity: 'success' });
      setDialog(null);
      qc.invalidateQueries({ queryKey: ['event-types'] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/event-types/${id}`)).data,
    onSuccess: () => {
      toast({ message: 'Event removed', severity: 'info' });
      qc.invalidateQueries({ queryKey: ['event-types'] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  return (
    <Box>
      <PageHeader
        title="Event library"
        subtitle="Standard track and field events plus your own custom ones."
        actions={
          isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialog({ mode: 'create' })}
              aria-label="Add custom event"
            >
              Add custom event
            </Button>
          )
        }
      />

      {user?.role === 'super_admin' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Tip: standard (global) events can only be edited from the Super admin → Event types tab.
          </Typography>
        </Box>
      )}

      {eventsQuery.isLoading ? (
        <LoadingFallback />
      ) : (eventsQuery.data ?? []).length === 0 ? (
        <EmptyState title="No events found" description="Custom events you create will show up here." />
      ) : (
        <Stack spacing={2}>
          {(['track', 'field', 'relay', 'multi'] as EventCategory[]).map((cat) =>
            (groups[cat] ?? []).length > 0 ? (
              <Card key={cat}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>{CATEGORY_LABELS[cat]}</Typography>
                  <Table size="small" aria-label={`${CATEGORY_LABELS[cat]} events`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Distance</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(groups[cat] ?? []).map((e) => {
                        const isCustom = !!e.org_id;
                        const canEdit = isAdmin && isCustom;
                        return (
                          <TableRow key={e.id}>
                            <TableCell><Chip size="small" label={e.code} variant="outlined" /></TableCell>
                            <TableCell>{e.name}</TableCell>
                            <TableCell>{e.distance_meters ? `${e.distance_meters} m` : '—'}</TableCell>
                            <TableCell>{e.unit}</TableCell>
                            <TableCell>
                              {isCustom ? <Chip size="small" label="Custom" /> : <Chip size="small" color="info" label="Standard" variant="outlined" />}
                            </TableCell>
                            <TableCell align="right">
                              {canEdit && (
                                <Tooltip title="Edit event">
                                  <IconButton
                                    aria-label={`Edit ${e.name}`}
                                    onClick={() => setDialog({ mode: 'edit', event: e })}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canEdit && (
                                <Tooltip title="Delete event">
                                  <IconButton
                                    aria-label={`Delete ${e.name}`}
                                    onClick={async () => {
                                      const ok = await confirm({
                                        title: 'Delete event?',
                                        message: `Permanently delete custom event "${e.name}"?`,
                                        destructive: true,
                                      });
                                      if (ok) deleteMutation.mutate(e.id);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : null,
          )}
        </Stack>
      )}

      {dialog && (
        <EventFormDialog
          open
          mode={dialog.mode}
          event={dialog.mode === 'edit' ? dialog.event : null}
          onClose={() => setDialog(null)}
          onSubmit={(payload) => {
            if (dialog.mode === 'edit') {
              updateMutation.mutate({ id: dialog.event.id, payload });
            } else {
              createMutation.mutate(payload);
            }
          }}
          busy={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </Box>
  );
}

function EventFormDialog({
  open,
  mode,
  event,
  onClose,
  onSubmit,
  busy,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  event: EventType | null;
  onClose: () => void;
  onSubmit: (p: EventFormPayload) => void;
  busy: boolean;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<EventCategory>('track');
  const [unit, setUnit] = useState('seconds');
  const [distance, setDistance] = useState('');
  const [isRelay, setIsRelay] = useState(false);

  useEffect(() => {
    if (open) {
      setCode(event?.code ?? '');
      setName(event?.name ?? '');
      setCategory((event?.category as EventCategory) ?? 'track');
      setUnit(event?.unit ?? 'seconds');
      setDistance(event?.distance_meters?.toString() ?? '');
      setIsRelay(event?.is_relay ?? false);
    }
  }, [open, event]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="event-form-title">
      <DialogTitle id="event-form-title">
        {mode === 'edit' ? `Edit ${event?.name}` : 'Add custom event'}
      </DialogTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            code: code.trim().toUpperCase(),
            name: name.trim(),
            category,
            unit,
            is_relay: isRelay,
            distance_meters: distance ? Number(distance) : undefined,
          });
        }}
        aria-label="Event form"
      >
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                fullWidth
                inputProps={{ 'aria-label': 'Event code' }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                inputProps={{ 'aria-label': 'Event name' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Category"
                select
                value={category}
                onChange={(e) => setCategory(e.target.value as EventCategory)}
                fullWidth
              >
                <MenuItem value="track">Track</MenuItem>
                <MenuItem value="field">Field</MenuItem>
                <MenuItem value="relay">Relay</MenuItem>
                <MenuItem value="multi">Multi-events</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Unit"
                select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                fullWidth
              >
                <MenuItem value="seconds">Seconds (time)</MenuItem>
                <MenuItem value="meters">Meters (distance/height)</MenuItem>
                <MenuItem value="points">Points</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Distance (m, optional)"
                type="number"
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Is a relay?"
                select
                value={isRelay ? 'yes' : 'no'}
                onChange={(e) => setIsRelay(e.target.value === 'yes')}
                fullWidth
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} aria-label="Cancel">Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy} aria-label={mode === 'edit' ? 'Save event' : 'Add event'}>
            {busy ? 'Saving…' : mode === 'edit' ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
